
(() => {
  const endpoint = 'https://pintryx-review-translator.pintryx.workers.dev/translate';
  const locale = (document.documentElement.lang || '').toLowerCase().split('-')[0];

  if (locale !== 'en' && locale !== 'fr') return;

  const reviewTextSelector = [
    '.jdgm-rev__title',
    '.jdgm-rev__body',
    '.jdgm-full-rev__title',
    '.jdgm-full-rev__body',
    '.jm-review-content__title',
    '.jdgm-review-content__body-content',
    '.jdgm-review-card__title',
    '.jdgm-review-card__body',
    '.jdgm-card .jdgm-text',
    '.jdgm-rev-snippet-card__title',
    '.jdgm-rev-snippet-card__body',
  ].join(',');

  const reviewSelector = [
    '.jdgm-rev',
    '.jdgm-full-rev',
    '.jdgm-card',
    '.jdgm-rev-snippet-card',
    '.jm-review-item',
    '.jm-carousel-review-item',
    '.jm-align-review-item',
    '.jdgm-review-card__content',
  ].join(',');

  const states = new WeakMap();
  const queue = [];
  let flushTimer;
  let isFlushing = false;

  const labels = locale === 'fr'
    ? { original: 'Voir l\u2019original', translated: 'Voir la traduction' }
    : { original: 'Show original', translated: 'Show translation' };

  const widgetUiTranslations = locale === 'fr'
    ? new Map([
        ['customer reviews', 'Avis clients'],
        ['write a review', '\u00c9crire un avis'],
        ['show more', 'Afficher plus'],
        ['more', 'Plus'],
        ['verified', 'V\u00e9rifi\u00e9'],
        ['anonymous', 'Anonyme'],
        ['review written on shop app', 'Avis \u00e9crit sur Shop App'],
        ['reply from pintryx:', 'R\u00e9ponse de Pintryx :'],
      ])
    : new Map([
        ['avis clients', 'Customer Reviews'],
        ['avis client', 'Customer Reviews'],
        ['\u00e9crire un avis', 'Write a review'],
        ['afficher plus', 'Show more'],
        ['plus', 'More'],
        ['v\u00e9rifi\u00e9', 'Verified'],
        ['anonyme', 'Anonymous'],
        ['avis \u00e9crit sur shop app', 'Review written on Shop App'],
        ['r\u00e9ponse de pintryx:', 'Reply from Pintryx:'],
      ]);

  const englishJudgeMeFormSettings = {
    review_content_screen_title_text: 'How would you rate this product?',
    review_content_introduction_text: "We'd love for you to share a little about your experience.",
    store_review_form_title_text: 'How would you rate this store?',
    store_review_form_introduction_text: "We'd love for you to share a little about your experience.",
    one_star_review_guidance_text: 'Poor',
    five_star_review_guidance_text: 'Excellent',
    customer_information_screen_title_text: 'About you',
    customer_information_introduction_text: 'Please tell us a little about yourself.',
    custom_questions_screen_title_text: 'Your experience in detail',
    custom_questions_introduction_text: 'Here are a few questions to help us understand your experience.',
    review_submitted_screen_title_text: 'Thanks for your review!',
    review_submitted_screen_thank_you_text: "We're processing it and it will appear in the store soon.",
    review_submitted_screen_email_verification_text: 'Please confirm your email using the link we just sent. This helps us keep reviews authentic.',
    confirm_email_screen_title_text: 'Confirm your email',
    confirm_email_screen_message_text: "To keep reviews authentic, we'll email you a secure link to continue writing your review. It only takes a moment.",
    check_email_screen_title_text: 'Check your email',
    check_email_screen_message_text: 'We sent an email to {{ email }}. Click the button in the email to continue.',
    check_email_screen_resend_message_text: 'Email resent!',
    check_email_resend_hint_text: "Didn't receive the email? Check your spam folder or [resend the email].",
    verification_email_rate_limit_error_text: "You've reached the review-attempt limit for this product. Check your inbox or try again later.",
    review_submitted_request_store_review_text: 'Would you like to share your shopping experience with us?',
    review_submitted_review_other_products_text: 'Would you like to review these products?',
    store_review_screen_title_text: 'Would you like to share your shopping experience with us?',
    store_review_introduction_text: 'We value your feedback and use it to improve. Please share your thoughts or suggestions.',
    reviewer_media_screen_title_picture_text: 'Share a photo',
    reviewer_media_introduction_picture_text: 'Upload a photo to support your review.',
    reviewer_media_screen_title_video_text: 'Share a video',
    reviewer_media_introduction_video_text: 'Upload a video to support your review.',
    reviewer_media_screen_title_picture_or_video_text: 'Share a photo or video',
    reviewer_media_introduction_picture_or_video_text: 'Upload a photo or video to support your review.',
    reviewer_media_youtube_url_text: 'Paste your YouTube URL here',
    advanced_settings_next_step_button_text: 'Next',
    advanced_settings_close_review_button_text: 'Close',
    write_review_flow_required_text: 'Required',
    write_review_flow_privacy_message_text: 'We respect your privacy.',
    write_review_flow_anonymous_text: 'Anonymous review',
    write_review_flow_visibility_text: 'Will not be visible to other customers.',
    write_review_flow_multiple_selection_help_text: 'Select as many as you like',
    write_review_flow_single_selection_help_text: 'Select one option',
    write_review_flow_required_field_error_text: 'This field is required',
    write_review_flow_invalid_email_error_text: 'Please enter a valid email address',
    write_review_flow_max_length_error_text: 'Maximum {{ max_length }} characters.',
    write_review_flow_media_upload_text: '<b>Click to upload</b> or drag and drop',
    write_review_flow_gdpr_statement: "We'll contact you about your review only if necessary. By submitting your review, you agree to our <a href='https://judge.me/terms' target='_blank' rel='nofollow noopener'>terms of use</a> and <a href='https://judge.me/privacy' target='_blank' rel='nofollow noopener'>privacy policy</a>.",
    widget_rating_field_text: 'Rating',
    widget_review_title_field_text: 'Review title',
    widget_review_title_placeholder_text: 'Give your review a title',
    widget_review_body_field_text: 'Review content',
    widget_review_body_placeholder_text: 'Start writing here...',
    widget_name_field_text: 'Display name',
    widget_name_placeholder_text: 'Display name',
    widget_email_field_text: 'Email address',
    widget_email_placeholder_text: 'Your email address',
    widget_email_field_error_text: 'Please enter a valid email address.',
    widget_verified_name_field_text: 'Verified name (public)',
    widget_verified_email_field_text: 'Verified email (private, cannot be changed)',
    widget_public_name_text: 'displayed publicly as',
    widget_pictures_field_text: 'Photo/video (optional)',
    widget_required_field_error_text: 'This field is required.',
    widget_invalid_yt_video_url_error_text: 'Not a valid YouTube video URL',
    widget_media_field_exceed_quantity_message: 'Sorry, we can only accept {{ max_media }} per review.',
    widget_media_field_exceed_limit_message: '{{ file_name }} is too large. Please select a {{ media_type }} under {{ size_limit }}MB.',
    widget_back_button_text: 'Back',
    widget_next_button_text: 'Next',
    widget_close_form_text: 'Cancel review',
    widget_submit_review_text: 'Submit review',
    widget_submit_verified_review_text: 'Submit a verified review',
    widget_review_submitted_text: 'Review submitted!',
    widget_submit_success_msg_no_auto_publish: "Thanks! Your review will be published after the store administrator approves it. You can delete or edit your review by signing in to <a href='https://judge.me/login' target='_blank' rel='nofollow noopener'>Judge.me</a>.",
    widget_submit_success_msg_with_auto_publish: "Thanks! Refresh the page in a few moments to see your review. You can delete or edit your review by signing in to <a href='https://judge.me/login' target='_blank' rel='nofollow noopener'>Judge.me</a>.",
  };

  const frenchWords = new Set([
    'avec', 'bien', 'bon', 'bonne', 'ce', 'ces', 'cette', 'dans', 'de', 'des', 'du', 'elle', 'en', 'est',
    'et', 'facile', 'fait', 'faire', 'fonctionne', 'il', 'je', 'la', 'le', 'les', 'livraison', 'mais', 'merci',
    'mon', 'pas', 'plus', 'pour', 'produit', 'qualite', 'que', 'qui', 'rapide', 'recommande', 'sur', 'tres',
    'tout', 'tous', 'une', 'un', 'utiliser', 'vraiment',
  ]);

  const englishWords = new Set([
    'a', 'also', 'amazing', 'and', 'are', 'as', 'easy', 'excellent', 'fast', 'for', 'good', 'great', 'has',
    'have', 'helpful', 'highly', 'i', 'in', 'is', 'it', 'my', 'of', 'product', 'quality', 'really', 'recommend',
    'shipping', 'that', 'the', 'these', 'they', 'this', 'to', 'use', 'very', 'was', 'with', 'works', 'you',
  ]);

  function normalizedWords(text) {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .match(/[a-z]+/g) || [];
  }

  function detectLanguage(text) {
    const original = text.toLowerCase();
    const words = normalizedWords(text);
    let frenchScore = /[\u00e0\u00e2\u00e7\u00e9\u00e8\u00ea\u00eb\u00ee\u00ef\u00f4\u00f9\u00fb\u00fc\u00ff\u0153\u00e6]/i.test(original) ? 3 : 0;
    let englishScore = 0;

    words.forEach((word) => {
      if (frenchWords.has(word)) frenchScore += 1;
      if (englishWords.has(word)) englishScore += 1;
    });

    if (/\b(c['\u2019]|j['\u2019]|l['\u2019]|qu['\u2019]|n['\u2019]|d['\u2019])/.test(original)) frenchScore += 2;
    if (frenchScore > englishScore && frenchScore >= 1) return 'fr';
    if (englishScore > frenchScore && englishScore >= 1) return 'en';
    return '';
  }

  function normalizedLabel(text) {
    return text
      .trim()
      .replace(/\s+/g, ' ')
      .replace(/\s+:/g, ':')
      .toLocaleLowerCase(locale === 'fr' ? 'fr' : 'en');
  }

  function applyStorefrontFormLocale() {
    if (locale !== 'en' || !window.jdgmSettings) return;

    Object.entries(englishJudgeMeFormSettings).forEach(([key, englishValue]) => {
      const currentValue = window.jdgmSettings[key];

      // Preserve the original French copy as a DOM fallback in case Judge.me
      // mounted part of the modal before reading the updated settings object.
      if (typeof currentValue === 'string' && currentValue && !/<[a-z][\s\S]*>/i.test(currentValue)) {
        widgetUiTranslations.set(normalizedLabel(currentValue), englishValue);
      }

      window.jdgmSettings[key] = englishValue;
    });

    // This changes only the current browser runtime; it does not update the
    // Judge.me account language or the language used for customer emails.
    window.jdgmSettings.shop_locale = 'en';
  }

  function translatedUiLabel(text) {
    const normalized = normalizedLabel(text);
    const exact = widgetUiTranslations.get(normalized);
    if (exact) return exact;

    if (locale === 'en') {
      const reviewCount = normalized.match(/^(\d+)\s+avis$/);
      if (reviewCount) return `${reviewCount[1]} ${reviewCount[1] === '1' ? 'review' : 'reviews'}`;
    } else {
      const reviewCount = normalized.match(/^(\d+)\s+reviews?$/);
      if (reviewCount) return `${reviewCount[1]} avis`;
    }

    return '';
  }

  function isReviewCopy(node) {
    const parent = node.parentElement;
    if (!parent) return false;

    return Boolean(parent.closest([
      reviewTextSelector,
      '.jm-shop-reply__text',
      '.jdgm-rev__reply',
    ].join(',')));
  }

  // FIX: Only scan actual Judge.me UI containers.
  const judgeMeWidgetSelector = [
    '.jdgm-review-widget',
    '.jdgm-review-widget-modal',
    '.jdgm-write-review-modal',
    '.jm-modal',
    '.jdgm-cards-carousel',
    '.jdgm-testimonials-carousel',
  ].join(',');

  function translateWidgetUi(root) {
    if (!(root instanceof Element || root instanceof Document)) return;

    // Scan only the changed subtree when it belongs to Judge.me.
    const roots = root instanceof Element && root.closest(judgeMeWidgetSelector)
      ? [root]
      : Array.from(root.querySelectorAll(judgeMeWidgetSelector));

    roots.forEach((widgetRoot) => {
      const walker = document.createTreeWalker(widgetRoot, NodeFilter.SHOW_TEXT);
      let textNode;

      while ((textNode = walker.nextNode())) {
        if (isReviewCopy(textNode)) continue;

        const value = textNode.nodeValue || '';
        const replacement = translatedUiLabel(value);
        if (!replacement) continue;

        const leading = value.match(/^\s*/)?.[0] || '';
        const trailing = value.match(/\s*$/)?.[0] || '';
        const newValue = `${leading}${replacement}${trailing}`;

        // FIX: Prevent unnecessary mutations and observer feedback loops.
        if (newValue !== value) textNode.nodeValue = newValue;
      }
    });
  }

  function reviewTextOverride(text) {
    const normalized = normalizedLabel(text);

    if (locale === 'en' && normalized === 'avis laiss\u00e9 sans commentaire') {
      return 'Left no comments';
    }

    if (locale === 'fr' && normalized === 'left no comments') {
      return 'Avis laiss\u00e9 sans commentaire';
    }

    return '';
  }

  function preserveEmoji(original, translated) {
    const emojiPattern = /(?:\p{Regional_Indicator}{2}|\p{Extended_Pictographic}(?:\uFE0F|\uFE0E)?(?:\u200D\p{Extended_Pictographic}(?:\uFE0F|\uFE0E)?)*)/gu;
    const originalEmoji = original.match(emojiPattern) || [];
    if (!originalEmoji.length) return translated;

    let comparison = translated;
    const missing = [];

    originalEmoji.forEach((emoji) => {
      if (comparison.includes(emoji)) {
        comparison = comparison.replace(emoji, '');
      } else {
        missing.push(emoji);
      }
    });

    return missing.length ? `${translated.trim()} ${missing.join(' ')}` : translated;
  }

  function getReviewLanguage(element) {
    const review = element.closest(reviewSelector);
    const declared = (review?.dataset.reviewLanguage || '').toLowerCase().split('-')[0];

    if (declared === 'en' || declared === 'fr') return declared;

    const body = review?.querySelector([
      '.jdgm-rev__body',
      '.jdgm-full-rev__body',
      '.jdgm-review-content__body-content',
      '.jdgm-review-card__body',
      '.jdgm-text',
    ].join(','));

    return detectLanguage(body?.textContent || element.textContent || '');
  }

  function shouldTranslate(element) {
    const sourceLanguage = getReviewLanguage(element);

    if (sourceLanguage === locale) return false;
    if (sourceLanguage) return true;

    // Leave ambiguous reviews alone on the English storefront.
    return locale === 'fr';
  }

  function addToggle(element, state) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'pintryx-review-translation-toggle';
    button.textContent = labels.original;
    button.setAttribute('aria-expanded', 'false');

    button.addEventListener('click', () => {
      state.showingOriginal = !state.showingOriginal;

      if (state.showingOriginal) {
        element.innerHTML = state.originalHtml;
        button.textContent = labels.translated;
        button.setAttribute('aria-expanded', 'true');
      } else {
        element.textContent = state.translation;
        button.textContent = labels.original;
        button.setAttribute('aria-expanded', 'false');
      }
    });

    element.insertAdjacentElement('afterend', button);
    state.button = button;
  }

  function applyTranslation(item, translation) {
    const { element, originalHtml, originalText } = item;

    if (!element.isConnected || !translation || translation.trim() === originalText) {
      element.dataset.pintryxTranslationState = 'done';
      return;
    }

    const state = {
      originalHtml,
      originalText,
      translation: preserveEmoji(originalText, translation.trim()),
      showingOriginal: false,
      button: null,
    };

    states.set(element, state);
    element.textContent = state.translation;
    element.dataset.pintryxTranslationState = 'translated';
    addToggle(element, state);
  }

  function markBatchFailed(batch) {
    batch.forEach(({ element }) => {
      if (element.isConnected) element.dataset.pintryxTranslationState = 'failed';
    });
  }

  async function translateBatch(batch) {
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          target: locale,
          texts: batch.map((item) => item.originalText),
        }),
      });

      if (!response.ok) {
        throw new Error(`Translation request failed: ${response.status}`);
      }

      const data = await response.json();

      if (!Array.isArray(data.translations) || data.translations.length !== batch.length) {
        throw new Error('Translation response was incomplete');
      }

      batch.forEach((item, index) => {
        applyTranslation(item, data.translations[index]);
      });
    } catch (error) {
      markBatchFailed(batch);
      console.warn('Pintryx review translation unavailable; showing original reviews.', error);
    }
  }

  async function flushQueue() {
    if (isFlushing) return;

    isFlushing = true;
    clearTimeout(flushTimer);

    while (queue.length) {
      const batch = [];
      let characters = 0;

      while (queue.length && batch.length < 4) {
        const next = queue[0];

        if (batch.length && characters + next.originalText.length > 3500) break;

        queue.shift();
        batch.push(next);
        characters += next.originalText.length;
      }

      await translateBatch(batch);
    }

    isFlushing = false;
  }

  function queueElement(element) {
    if (!(element instanceof HTMLElement)) return;
    if (element.dataset.pintryxTranslationState) return;

    // Skip Judge.me's hidden legacy review copy.
    if (element.closest('.jdgm-ssr-reviews')) {
      element.dataset.pintryxTranslationState = 'done';
      return;
    }

    const originalText = (element.textContent || '').replace(/\s+/g, ' ').trim();

    if (!originalText) return;

    if (originalText.length > 1000 || !shouldTranslate(element)) {
      element.dataset.pintryxTranslationState = 'done';
      return;
    }

    element.dataset.pintryxTranslationState = 'pending';

    const item = {
      element,
      originalHtml: element.innerHTML,
      originalText,
    };

    const override = reviewTextOverride(originalText);

    if (override) {
      applyTranslation(item, override);
      return;
    }

    queue.push(item);

    clearTimeout(flushTimer);
    flushTimer = setTimeout(flushQueue, 80);
  }

  function scan(root) {
    if (!(root instanceof Element || root instanceof Document)) return;

    if (root instanceof Element && root.matches(reviewTextSelector)) {
      queueElement(root);
    }

    root.querySelectorAll(reviewTextSelector).forEach(queueElement);
  }

  function addStyles() {
    const style = document.createElement('style');

    style.textContent = `
      .pintryx-review-translation-toggle {
        appearance: none;
        background: none;
        border: 0;
        color: currentColor;
        cursor: pointer;
        display: block;
        font: inherit;
        font-size: 0.82em;
        margin: 0.35rem 0 0;
        opacity: 0.72;
        padding: 0;
        text-decoration: underline;
        text-underline-offset: 0.16em;
      }

      .pintryx-review-translation-toggle:hover,
      .pintryx-review-translation-toggle:focus-visible {
        opacity: 1;
      }
    `;

    document.head.appendChild(style);
  }

  function start() {
    addStyles();

    translateWidgetUi(document);
    scan(document);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        const target = mutation.target.nodeType === Node.ELEMENT_NODE
          ? mutation.target
          : mutation.target.parentElement;

        // FIX: Ignore mutations outside Judge.me.
        const targetIsReview = target instanceof Element && (
          target.closest(judgeMeWidgetSelector) ||
          target.closest(reviewSelector) ||
          target.matches(reviewTextSelector)
        );

        if (targetIsReview) {
          scan(target);
          translateWidgetUi(target);
        }

        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;

          const isReviewNode =
            node.closest(judgeMeWidgetSelector) ||
            node.closest(reviewSelector) ||
            node.matches(reviewTextSelector) ||
            node.querySelector(
              [judgeMeWidgetSelector, reviewSelector, reviewTextSelector].join(',')
            );

          if (isReviewNode) {
            scan(node);
            translateWidgetUi(node);
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      characterData: true,
      subtree: true,
    });
  }

  applyStorefrontFormLocale();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
